import {
  IconTags,
  IconMessageChatbot,
  IconTimelineEvent,
  IconUsersGroup,
  IconVersions,
  IconNotebook,
  IconMessage2,
  IconSpeakerphone,
  IconTicket,
  IconSettings,
  IconChecklist,
  IconCalendarCog,
  IconContract,
  IconLanguage,
  IconGift,
  IconLayoutDashboard,
  IconMail,
  IconChartBar,
  IconTemplate,
  IconScan,
  IconIdBadge2,
  IconUserPlus,
  IconClipboardText,
  IconLayoutGrid,
  IconShieldCheck,
  IconListCheck,
} from "@tabler/icons-react";
import { FEATURE_FLAGS } from "@congrs/schemas";
import { webConfig } from "../../../config";

const sections = [
  {
    id: "system",
    label: "System",
    roles: ["sysAdmin"],
    items: [
      {
        path: "/app/users",
        label: "Users",
        icon: IconUsersGroup,
      },
      {
        path: "/app/system-settings",
        label: "System Configuration",
        icon: IconSettings,
      },
      {
        path: "/app/languages",
        label: "Languages",
        icon: IconLanguage,
      },
      {
        path: "/app/template-previews",
        label: "Template Previews",
        icon: IconTemplate,
      },
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    items: [
      {
        path: "/app",
        label: "Organization",
        icon: IconLayoutDashboard,
      },
    ],
  },
  {
    id: "setup",
    label: "Setup",
    roles: ["sysAdmin", "wsAdmin"],
    // Global switch in System Configuration
    requiresSetting: "enableSetupScreen",
    items: [
      {
        path: "/app/setup",
        label: "Setup",
        icon: IconListCheck,
      },
    ],
  },
  {
    id: "registrations",
    label: "Registrations",
    items: [
      {
        path: "/app/registrations/invite-attendees",
        label: "Invite Attendees",
        icon: IconUserPlus,
        featureFlag: FEATURE_FLAGS.INVITE_EMAILS,
      },
      {
        path: "/app/registrations/form",
        label: "Registration Form",
        icon: IconClipboardText,
        featureFlag: FEATURE_FLAGS.REGISTRATIONS,
      },
      {
        path: "/app/registrations/overview",
        label: "Overview",
        icon: IconLayoutGrid,
        featureFlag: FEATURE_FLAGS.REGISTRATIONS,
      },
    ],
  },
  {
    id: "eventManagement",
    label: "Event Management",
    items: [
      {
        path: "/app/events/settings",
        label: "Event Settings",
        icon: IconCalendarCog,
      },
      {
        path: "/app/tickets",
        label: "Tickets",
        icon: IconTicket,
      },
      {
        path: "/app/new-tickets",
        label: "New Tickets",
        icon: IconTicket,
        devOnly: true,
      },
      {
        path: "/app/speakers",
        label: "Speakers",
        icon: IconSpeakerphone,
      },
      {
        path: "/app/sponsors",
        label: "Sponsors",
        icon: IconVersions,
      },
      {
        path: "/app/sessions",
        label: "Sessions",
        icon: IconTimelineEvent,
      },
      {
        path: "/app/campaigns",
        label: "Campaigns",
        icon: IconMail,
      },
      {
        path: "/app/compliance",
        label: "Compliance",
        icon: IconShieldCheck,
        featureFlag: FEATURE_FLAGS.EVENT_COMPLIANCE,
      },
    ],
  },
  {
    id: "checkIn",
    label: "Check-in",
    items: [
      {
        path: "/app/check-in",
        label: "Check-in",
        icon: IconScan,
        featureFlag: FEATURE_FLAGS.CHECK_IN,
      },
      {
        path: "/app/badges",
        label: "Badges",
        icon: IconIdBadge2,
        featureFlag: FEATURE_FLAGS.BADGES,
      },
    ],
  },
  {
    id: "content",
    label: "Content",
    items: [
      {
        path: "/app/pages",
        label: "Pages",
        icon: IconNotebook,
      },
      {
        path: "/app/documents",
        label: "Documents",
        icon: IconContract,
        featureFlag: FEATURE_FLAGS.DOCUMENTS,
      },
      {
        path: "/app/announcements",
        label: "Announcements",
        icon: IconMessage2,
      },
    ],
  },
  {
    id: "engagement",
    label: "Engagement",
    items: [
      {
        path: "/app/tags",
        label: "Tags",
        icon: IconTags,
      },
      {
        path: "/app/surveys",
        label: "Surveys",
        icon: IconChecklist,
        featureFlag: FEATURE_FLAGS.SURVEYS,
      },
      {
        path: "/app/polls",
        label: "Polls",
        icon: IconMessageChatbot,
        featureFlag: FEATURE_FLAGS.POLLS,
      },
      {
        path: "/app/giveaway",
        label: "Giveaway",
        icon: IconGift,
        featureFlag: FEATURE_FLAGS.GIVEAWAYS,
      },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    items: [
      {
        path: "/app/insights",
        label: "Insights",
        icon: IconChartBar,
      },
    ],
  },
];

const filterItems = (items, role, featureFlags, isDevelopment) => {
  return items.filter((item) => {
    // Filter out dev-only items in production
    if (item.devOnly && !isDevelopment) {
      return false;
    }

    // Filter by specific roles
    if (item.roles) {
      return item.roles.includes(role);
    }

    // Filter by feature flags
    if (item.featureFlag) {
      if (!featureFlags || !Array.isArray(featureFlags)) return false;
      const flag = featureFlags.find((f) => f.featureName === item.featureFlag);
      if (!flag || !flag.enabled) return false;
    }

    return true;
  });
};

export const filteredSections = (role, featureFlags, systemSettings) => {
  if (!role) {
    return null;
  }

  const isDevelopment = !webConfig.isProduction;

  // Special case for ticketAdmin
  if (role === "ticketAdmin") {
    return [
      {
        id: "tickets",
        label: "Tickets",
        items: [
          {
            path: "/app/tickets",
            label: "Tickets",
            icon: IconTicket,
          },
        ],
      },
    ];
  }

  return sections
    .filter((section) => {
      // Filter sections by role
      if (section.roles && !section.roles.includes(role)) {
        return false;
      }

      // Filter sections that need a global switch turned on
      if (
        section.requiresSetting &&
        !systemSettings?.[section.requiresSetting]
      ) {
        return false;
      }

      // Filter sections by feature flag
      if (section.featureFlag) {
        if (!featureFlags || !Array.isArray(featureFlags)) return false;
        const flag = featureFlags.find(
          (f) => f.featureName === section.featureFlag
        );
        if (!flag || !flag.enabled) return false;
      }

      return true;
    })
    .map((section) => ({
      ...section,
      items: filterItems(section.items, role, featureFlags, isDevelopment),
    }))
    .filter((section) => section.items.length > 0);
};

export default sections;
